import * as React from "react";
import { useSelector } from "react-redux";
import { Box, Divider, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

import EconomicValueChainStageChart from "./Charts/EconomicValueChainStageChart";
import EconomicBeneficiaryChart from "./Charts/EconomicBeneficiaryChart";

import Circle from "../../../../components/Icons/Circle";

import { StyledItemGraph } from "../../../../components/styles/StyledItemGraph";
import { Bubble } from "../../../../components/Icons/Bubble";
import { RootState } from "../../../../store/store";

import theme from "../../../../components/styles/theme/theme";
import { CURRENCY } from "../../../../config";
import { autoDenominate } from "../../../../utils/charts/formatChartValues";

export const EconomicSection: React.FC = () => {
  const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return !isSmScreen ? (
    <>
      <Box id="economicSection" sx={{ flexGrow: 1, padding: '130px 25px 0px 25px' }} >
        <Grid item xs={2} sm={2} md={1}>
          <Box sx={{ width: 1 }}>
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
              <Box gridColumn="span 12">
                <StyledItemGraph>
                  <EconomicChartSection />
                </StyledItemGraph>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>

      {!isSmScreen && (
        <Bubble
          style={{
            position: "absolute",
            right: "115px",
            transform: "translateY(-140px)",
            zIndex: -1,
          }}
        />
      )}
    </>
  ) : (
    <EconomicChartSection />
  );
};

const EconomicChartSection: React.FC = () => {
  const { t } = useTranslation();
  const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    statistics: { statisticsForSelectedCountry },
  } = useSelector((state: RootState) => state);

  const valueInMillion = t("charts.in_million_currency", {
    currency: CURRENCY,
  });

  // Auto-denominate: if values >= 1000M, show as billions
  const vcl = statisticsForSelectedCountry?.va_valueChainLink;
  const vclUnit = vcl ? autoDenominate(
    [vcl.upstream_suppliers_and_outlets, vcl.upstream_suppliers_and_outlets_suppliers,
     vcl.theSystem_theCocaColaSystem, vcl.downstream_suppliers_and_outlets,
     vcl.downstream_suppliers_and_outlets_suppliers, vcl.downstream_Vat],
    vcl.display_values_in
  ).unit : valueInMillion;

  const vbb = statisticsForSelectedCountry?.va_byBeneficiary;
  const vbbUnit = vbb ? autoDenominate(
    [vbb.salaries_theCocaColaSystem, vbb.profits_TheCocaColaSystem, vbb.taxes_theCocaColaSytem,
     vbb.salaries_suppliers_and_outlets, vbb.profits_suppliers_and_outlets, vbb.taxes_suppliers_and_outlets,
     vbb.salaries_suppliers_and_outlets_suppliers, vbb.profits_suppliers_and_outlets_suppliers, vbb.taxes_suppliers_and_outlets_suppliers],
    vbb.display_values_in
  ).unit : valueInMillion;

  return (
    <Grid
      container
      spacing={{ xs: 2, md: 2 }}
      columns={{ xs: 13, sm: 13, md: 13 }}
      justifyContent="center"
      padding="20px 40px"
    >
      <Grid
        item
        xs={13}
        sm={13}
        md={13}
        paddingBottom={!isSmScreen ? "40px" : "10px"}
      >
        <Typography
          variant="h5"
          component="h5"
          fontWeight="600"
          color={(theme) => theme.palette.secondary.main}
        >
          {t("charts.economic_value_added")}
        </Typography>
      </Grid>

      <Grid
        item
        xs={13}
        sm={13}
        md={6}
        textAlign="start"
        paddingBottom={isSmScreen ? "40px" : 0}
      >
        <Typography
          variant="body2"
          component="span"
          fontWeight="600"
          sx={{ fontSize: "16px" }}
        >
          {t("charts.value_added")}
          <Circle
            color={theme.palette.secondary.main}
            width="8px"
            height="8px"
            style={{ margin: "0 8px" }}
          />
        </Typography>
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.primary["500"]}
        >
          {t("charts.by_value_chain_stage")}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          color={theme.palette.primary["500"]}
        >
          {vclUnit}
        </Typography>
        <Box padding={!isSmScreen ? "25px" : 0}>
          <EconomicValueChainStageChart />
        </Box>
      </Grid>

      {!isSmScreen && (
        <Divider orientation="vertical" flexItem sx={{ margin: "0 auto" }} />
      )}

      <Grid item xs={13} sm={13} md={6} textAlign="start">
        <Typography
          variant="body2"
          component="span"
          fontWeight="600"
          sx={{ fontSize: "16px" }}
        >
          {t("charts.value_added")}
          <Circle
            color={theme.palette.secondary.main}
            width="8px"
            height="8px"
            style={{ margin: "0 8px" }}
          />
        </Typography>
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.primary["500"]}
        >
          {t("charts.by_beneficiary")}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          color={theme.palette.primary["500"]}
        >
          {vbbUnit}
        </Typography>
        <Box padding={!isSmScreen ? "25px" : 0}>
          <EconomicBeneficiaryChart />
        </Box>
      </Grid>
    </Grid>
  );
};
